import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../../../hooks/useAuth";
import {
  useProviderCalendar,
  useSaveProviderCalendarStep,
} from "../../provider-owner.hooks";
import type {
  ProviderCalendarDto,
  ProviderWorkingDay,
  ProviderWorkingDayDto,
} from "../../provider-owner.types";

const DAYS: ProviderWorkingDay[] = [
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
  "SUNDAY",
];

const cardStyle: React.CSSProperties = {
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: 16,
  padding: "1rem",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  border: "1px solid #d1d5db",
  borderRadius: 12,
  padding: "0.8rem 0.9rem",
  fontSize: "0.96rem",
  background: "#fff",
  color: "#0f172a",
};

const labelStyle: React.CSSProperties = {
  fontSize: "0.9rem",
  fontWeight: 600,
  color: "#334155",
};

const primaryButtonStyle: React.CSSProperties = {
  border: "none",
  borderRadius: 12,
  background: "#111827",
  color: "#fff",
  padding: "0.8rem 1rem",
  fontWeight: 700,
  cursor: "pointer",
};

const secondaryButtonStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  textDecoration: "none",
  border: "1px solid #d1d5db",
  borderRadius: 12,
  background: "#fff",
  color: "#111827",
  padding: "0.8rem 1rem",
  fontWeight: 700,
};

const defaultCalendar = (): ProviderCalendarDto => ({
  timezone: "Europe/London",
  slotIntervalMinutes: 30,
  bufferBeforeMinutes: 0,
  bufferAfterMinutes: 0,
  workingDays: DAYS.map((day) => ({
    dayOfWeek: day,
    isOpen: day !== "SUNDAY",
    openTime: "09:00",
    closeTime: "18:00",
  })),
});

export default function ProviderCalendarStepPage() {
  const { user } = useAuth();
  const providerId = user?.providerId ?? "";

  const calendarQuery = useProviderCalendar(providerId);
  const saveCalendarStep = useSaveProviderCalendarStep(providerId);

  const [form, setForm] = useState<ProviderCalendarDto>(defaultCalendar());
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    if (calendarQuery.data) {
      setForm(calendarQuery.data);
    }
  }, [calendarQuery.data]);

  const updateWorkingDay = (
    dayOfWeek: ProviderWorkingDayDto["dayOfWeek"],
    patch: Partial<ProviderWorkingDayDto>,
  ) => {
    setForm((current) => ({
      ...current,
      workingDays: current.workingDays.map((day) =>
        day.dayOfWeek === dayOfWeek ? { ...day, ...patch } : day,
      ),
    }));
  };

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    await saveCalendarStep.mutateAsync(form);
    setSavedAt(new Date().toLocaleTimeString());
  };

  if (!providerId) {
    return (
      <div style={cardStyle}>
        <p style={{ margin: 0, color: "#b91c1c" }}>
          No provider is linked to this account yet.
        </p>
      </div>
    );
  }

  if (calendarQuery.isLoading) {
    return (
      <div style={cardStyle}>
        <p style={{ margin: 0, color: "#475569" }}>Loading calendar settings...</p>
      </div>
    );
  }

  if (calendarQuery.isError) {
    return (
      <div style={cardStyle}>
        <p style={{ margin: 0, color: "#b91c1c" }}>
          Unable to load calendar settings. Please refresh and try again.
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gap: "1rem" }}>
      <section style={cardStyle}>
        <h2 style={{ margin: 0, color: "#0f172a", fontSize: "1.4rem" }}>
          Calendar and slots
        </h2>
        <p style={{ margin: "0.5rem 0 0", color: "#475569", lineHeight: 1.7 }}>
          Configure business hours, slot intervals, and booking buffers.
        </p>
      </section>

      <form onSubmit={handleSave} style={{ display: "grid", gap: "1rem" }}>
        <section style={cardStyle}>
          <div
            style={{
              display: "grid",
              gap: "0.85rem",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            }}
          >
            <div style={{ display: "grid", gap: "0.45rem" }}>
              <label style={labelStyle}>Timezone</label>
              <input
                style={inputStyle}
                value={form.timezone}
                onChange={(event) =>
                  setForm((current) => ({ ...current, timezone: event.target.value }))
                }
              />
            </div>

            <div style={{ display: "grid", gap: "0.45rem" }}>
              <label style={labelStyle}>Slot interval (minutes)</label>
              <input
                style={inputStyle}
                type="number"
                min={5}
                step={5}
                value={form.slotIntervalMinutes}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    slotIntervalMinutes: Number(event.target.value),
                  }))
                }
              />
            </div>

            <div style={{ display: "grid", gap: "0.45rem" }}>
              <label style={labelStyle}>Buffer before (minutes)</label>
              <input
                style={inputStyle}
                type="number"
                min={0}
                step={5}
                value={form.bufferBeforeMinutes}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    bufferBeforeMinutes: Number(event.target.value),
                  }))
                }
              />
            </div>

            <div style={{ display: "grid", gap: "0.45rem" }}>
              <label style={labelStyle}>Buffer after (minutes)</label>
              <input
                style={inputStyle}
                type="number"
                min={0}
                step={5}
                value={form.bufferAfterMinutes}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    bufferAfterMinutes: Number(event.target.value),
                  }))
                }
              />
            </div>
          </div>
        </section>

        <section style={cardStyle}>
          <h3 style={{ marginTop: 0, color: "#0f172a" }}>Working days</h3>

          <div style={{ display: "grid", gap: "0.85rem" }}>
            {form.workingDays.map((day) => (
              <div
                key={day.dayOfWeek}
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 14,
                  padding: "0.9rem",
                  background: "#f8fafc",
                  display: "grid",
                  gap: "0.85rem",
                  gridTemplateColumns: "1.2fr 1fr 1fr",
                  alignItems: "end",
                }}
              >
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.6rem",
                    fontWeight: 600,
                    color: "#334155",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={day.isOpen}
                    onChange={(event) =>
                      updateWorkingDay(day.dayOfWeek, { isOpen: event.target.checked })
                    }
                  />
                  {day.dayOfWeek}
                </label>

                <div style={{ display: "grid", gap: "0.45rem" }}>
                  <label style={labelStyle}>Open time</label>
                  <input
                    style={inputStyle}
                    type="time"
                    value={day.openTime}
                    disabled={!day.isOpen}
                    onChange={(event) =>
                      updateWorkingDay(day.dayOfWeek, { openTime: event.target.value })
                    }
                  />
                </div>

                <div style={{ display: "grid", gap: "0.45rem" }}>
                  <label style={labelStyle}>Close time</label>
                  <input
                    style={inputStyle}
                    type="time"
                    value={day.closeTime}
                    disabled={!day.isOpen}
                    onChange={(event) =>
                      updateWorkingDay(day.dayOfWeek, { closeTime: event.target.value })
                    }
                  />
                </div>
              </div>
            ))}
          </div>
        </section>

        <section style={cardStyle}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "0.75rem",
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <div style={{ color: "#475569" }}>
              {savedAt ? `Saved at ${savedAt}` : "No save yet"}
            </div>

            <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
              <button
                type="submit"
                style={primaryButtonStyle}
                disabled={saveCalendarStep.isPending}
              >
                {saveCalendarStep.isPending ? "Saving..." : "Save calendar"}
              </button>

              <Link to="/provider/onboarding/deposit-policy" style={secondaryButtonStyle}>
                Continue to deposit policy
              </Link>
            </div>
          </div>
        </section>
      </form>
    </div>
  );
}